import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getUsers, type UserSummary } from "@admin/lib/api";
import { formatCents, formatCredits, formatDate } from "@admin/lib/utils";
import { Button } from "@admin/components/ui/button";

function statusClass(status: string | null): string {
  if (!status) return "bg-muted text-muted-fg";
  if (status === "running") return "bg-emerald-500/20 text-emerald-300";
  if (status === "hibernated") return "bg-indigo-500/20 text-indigo-300";
  if (status === "error") return "bg-danger/20 text-danger";
  return "bg-amber-500/20 text-amber-200";
}

export default function UsersPage() {
  const [items, setItems] = useState<UserSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [err, setErr] = useState<string | null>(null);
  const limit = 50;

  useEffect(() => {
    setErr(null);
    getUsers(limit, offset)
      .then((data) => {
        setItems(data.items);
        setTotal(data.total);
      })
      .catch(() => setErr("Could not load users"));
  }, [offset]);

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold">Users ({total})</h1>
      {err && <p className="text-danger">{err}</p>}
      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-border bg-muted/50 text-muted-fg">
            <tr>
              <th className="px-4 py-3 font-medium">Email</th>
              <th className="px-4 py-3 font-medium">Registered</th>
              <th className="px-4 py-3 font-medium">Country</th>
              <th className="px-4 py-3 font-medium">City</th>
              <th className="px-4 py-3 font-medium">VM status</th>
              <th className="px-4 py-3 font-medium">Plan</th>
              <th className="px-4 py-3 font-medium">Sub status</th>
              <th className="px-4 py-3 font-medium">Spend</th>
              <th className="px-4 py-3 font-medium">Remaining</th>
              <th className="px-4 py-3 font-medium">Platform revenue</th>
              <th className="px-4 py-3 font-medium">Earned</th>
            </tr>
          </thead>
          <tbody>
            {items.map((row) => (
              <tr key={row.id} className="border-b border-border last:border-0">
                <td className="px-4 py-3">
                  <Link to={`/users/${row.id}`} className="text-accent hover:underline">
                    {row.email}
                  </Link>
                </td>
                <td className="px-4 py-3 text-muted-fg">{formatDate(row.createdAt)}</td>
                <td className="px-4 py-3 text-muted-fg">{row.signupCountry ?? "-"}</td>
                <td className="px-4 py-3 text-muted-fg">{row.signupCity ?? "-"}</td>
                <td className="px-4 py-3">
                  <span
                    className={`inline-block rounded-full px-2 py-0.5 text-xs ${statusClass(row.serverStatus)}`}
                  >
                    {row.serverStatus ?? "none"}
                  </span>
                </td>
                <td className="px-4 py-3">{row.planCode ?? "-"}</td>
                <td className="px-4 py-3 text-muted-fg">{row.subscriptionStatus ?? "-"}</td>
                <td className="px-4 py-3 tabular-nums">{formatCents(row.spendUsdCents)}</td>
                <td className="px-4 py-3 tabular-nums">{formatCredits(row.remainingCredits)}</td>
                <td className="px-4 py-3 tabular-nums">{formatCents(row.platformRevenueCents)}</td>
                <td className="px-4 py-3 tabular-nums">{formatCredits(row.earnedCredits)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex gap-2">
        <Button
          variant="outline"
          size="sm"
          disabled={offset === 0}
          onClick={() => setOffset(Math.max(0, offset - limit))}
        >
          Previous
        </Button>
        <Button
          variant="outline"
          size="sm"
          disabled={offset + limit >= total}
          onClick={() => setOffset(offset + limit)}
        >
          Next
        </Button>
      </div>
    </div>
  );
}
